"use server"

import { cookies } from "next/headers"
import { getServerAuthHeaders } from "@/lib/auth-server"
import { COMPILER_API_URL } from "@/lib/bff-proxy"

export interface CreateSessionActionBody {
  templateId: string
  autoCompile: boolean
  initialFiles: Array<{
    path: string
    content: string
    isAsset?: boolean
  }>
}

export async function createAndSaveSessionAction(body: CreateSessionActionBody) {
  const cookieStore = await cookies()
  const existingSessionId = cookieStore.get("editor_session")?.value
  const consented = cookieStore.get("cookie_consent")?.value === "true"

  if (!COMPILER_API_URL) {
    throw new Error("Compiler API URL is not configured")
  }

  const payload: any = {
    template_id: body.templateId,
    auto_compile: body.autoCompile,
    initial_files: body.initialFiles.map((f) => ({
      path: f.path,
      content: f.content,
      is_asset: f.isAsset,
    })),
  }


  const url = `${COMPILER_API_URL}/api/v1/sessions`
  console.log("[Server Action] POST create/resume session:", url)

  const response = await fetch(url, {
    method: "POST",
    headers: await getServerAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Compiler API error: ${response.status} - ${errorText}`)
  }

  const data = await response.json()

  // Save cookie if accepted
  if (consented && data.session_id) {
    cookieStore.set("editor_session", data.session_id, {
      httpOnly: true,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
      path: "/",
      maxAge: 60 * 60 * 24 * 30, // 30 days
    })
  }

  return data
}

/** Update the session's main file so the backend compiles the correct file.
 *  Tries the sub-resource endpoint first (/main-file), then falls back to
 *  the general session PATCH. */
export async function setSessionMainFileAction(sessionId: string, mainFile: string) {
  if (!COMPILER_API_URL) {
    throw new Error("Compiler API URL is not configured")
  }

  // Try /main-file sub-resource first (matches /auto-compile pattern)
  const subUrl = `${COMPILER_API_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/main-file`
  console.log("[Server Action] PATCH session main-file:", subUrl, mainFile)

  const subResponse = await fetch(subUrl, {
    method: "PATCH",
    headers: await getServerAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ main_file: mainFile }),
  })

  if (subResponse.ok) {
    return { success: true }
  }

  console.warn("[Server Action] /main-file PATCH failed:", subResponse.status, "— trying general session PATCH")

  // Fallback: general session PATCH
  const generalUrl = `${COMPILER_API_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}`
  const generalResponse = await fetch(generalUrl, {
    method: "PATCH",
    headers: await getServerAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ main_file: mainFile }),
  })

  if (!generalResponse.ok) {
    const errorText = await generalResponse.text()
    throw new Error(`Compiler API error: ${generalResponse.status} - ${errorText}`)
  }

  return { success: true }
}

export async function setAutoCompileAction(sessionId: string, enabled: boolean) {
  if (!COMPILER_API_URL) {
    throw new Error("Compiler API URL is not configured")
  }

  const url = `${COMPILER_API_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/auto-compile`
  console.log("[Server Action] PATCH auto-compile:", url)

  const response = await fetch(url, {
    method: "PATCH",
    headers: await getServerAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ enabled }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Compiler API error: ${response.status} - ${errorText}`)
  }

  return { success: true }
}

export async function deleteSessionAction(sessionId: string) {
  const cookieStore = await cookies()
  cookieStore.delete("editor_session")

  if (!COMPILER_API_URL) {
    throw new Error("Compiler API URL is not configured")
  }

  const url = `${COMPILER_API_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}`
  console.log("[Server Action] DELETE session:", url)

  const response = await fetch(url, {
    method: "DELETE",
    headers: await getServerAuthHeaders(),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Compiler API error: ${response.status} - ${errorText}`)
  }

  return { success: true }
}

export async function clearSessionAction() {
  const cookieStore = await cookies()
  cookieStore.delete("editor_session")
  return { success: true }
}

export async function saveSessionIdCookieAction(sessionId: string, consentedOverride?: boolean) {
  const cookieStore = await cookies()
  const consented = consentedOverride ?? (cookieStore.get("cookie_consent")?.value === "true")

  if (consented) {
    cookieStore.set("editor_session", sessionId, {
      httpOnly: true,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
      path: "/",
      maxAge: 60 * 60 * 24 * 30, // 30 days
    })
    return { success: true }
  }

  return { success: false, error: "Not consented" }
}
